import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projetosService } from '../services/projetosService';
import type {
  FiltrosProjetos, CriarProjetoRequest, AtualizarProjetoRequest,
  SalvarFaseRequest, SalvarTarefaRequest, AdicionarDependenciaRequest,
} from '../types';

const KEYS = {
  lista: ['projetos'] as const,
  projeto: (id: number) => ['projeto', id] as const,
  fases: (id: number) => ['projeto', id, 'fases'] as const,
  tarefas: (id: number) => ['projeto', id, 'tarefas'] as const,
  dependencias: (id: number) => ['projeto', id, 'dependencias'] as const,
};

// ── Lista + CRUD de projetos ───────────────────────────────────────────────

export function useProjetos(filtros: FiltrosProjetos = {}) {
  return useQuery({
    queryKey: [...KEYS.lista, filtros],
    queryFn: () => projetosService.listar(filtros),
  });
}

export function useProjeto(id: number | undefined) {
  return useQuery({
    queryKey: KEYS.projeto(id ?? 0),
    queryFn: () => projetosService.obter(id!),
    enabled: !!id,
  });
}

export function useProjetoMutations() {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: KEYS.lista });

  const criar = useMutation({
    mutationFn: (req: CriarProjetoRequest) => projetosService.criar(req),
    onSuccess: inval,
  });
  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: AtualizarProjetoRequest }) =>
      projetosService.atualizar(id, req),
    onSuccess: (_d, v) => { inval(); qc.invalidateQueries({ queryKey: KEYS.projeto(v.id) }); },
  });
  const excluir = useMutation({
    mutationFn: (id: number) => projetosService.excluir(id),
    onSuccess: inval,
  });
  return { criar, atualizar, excluir };
}

// ── Sub-recursos (fases / tarefas / dependências) ──────────────────────────

export function useFases(projetoId: number | undefined) {
  return useQuery({
    queryKey: KEYS.fases(projetoId ?? 0),
    queryFn: () => projetosService.listarFases(projetoId!),
    enabled: !!projetoId,
  });
}

export function useTarefas(projetoId: number | undefined) {
  return useQuery({
    queryKey: KEYS.tarefas(projetoId ?? 0),
    queryFn: () => projetosService.listarTarefas(projetoId!),
    enabled: !!projetoId,
  });
}

export function useDependencias(projetoId: number | undefined) {
  return useQuery({
    queryKey: KEYS.dependencias(projetoId ?? 0),
    queryFn: () => projetosService.listarDependencias(projetoId!),
    enabled: !!projetoId,
  });
}

export function useGantt(projetoId: number | undefined) {
  return useQuery({
    queryKey: ['projeto', projetoId ?? 0, 'gantt'],
    queryFn: () => projetosService.gantt(projetoId!),
    enabled: !!projetoId,
  });
}

/** Mutations dos sub-recursos. Invalida fases/tarefas/projeto (roll-up de %). */
export function useProjetoSubMutations(projetoId: number) {
  const qc = useQueryClient();
  const invalAll = () => {
    qc.invalidateQueries({ queryKey: KEYS.fases(projetoId) });
    qc.invalidateQueries({ queryKey: KEYS.tarefas(projetoId) });
    qc.invalidateQueries({ queryKey: KEYS.dependencias(projetoId) });
    qc.invalidateQueries({ queryKey: KEYS.projeto(projetoId) });
    qc.invalidateQueries({ queryKey: KEYS.lista });
  };

  const salvarFase = useMutation({
    mutationFn: ({ faseId, req }: { faseId?: number; req: SalvarFaseRequest }) =>
      faseId ? projetosService.atualizarFase(projetoId, faseId, req)
             : projetosService.criarFase(projetoId, req),
    onSuccess: invalAll,
  });
  const excluirFase = useMutation({
    mutationFn: (faseId: number) => projetosService.excluirFase(projetoId, faseId),
    onSuccess: invalAll,
  });
  const salvarTarefa = useMutation({
    mutationFn: ({ tarefaId, req }: { tarefaId?: number; req: SalvarTarefaRequest }) =>
      tarefaId ? projetosService.atualizarTarefa(projetoId, tarefaId, req)
               : projetosService.criarTarefa(projetoId, req),
    onSuccess: invalAll,
  });
  const excluirTarefa = useMutation({
    mutationFn: (tarefaId: number) => projetosService.excluirTarefa(projetoId, tarefaId),
    onSuccess: invalAll,
  });
  const adicionarDependencia = useMutation({
    mutationFn: (req: AdicionarDependenciaRequest) => projetosService.adicionarDependencia(projetoId, req),
    onSuccess: invalAll,
  });
  const removerDependencia = useMutation({
    mutationFn: (depId: number) => projetosService.removerDependencia(projetoId, depId),
    onSuccess: invalAll,
  });

  return { salvarFase, excluirFase, salvarTarefa, excluirTarefa, adicionarDependencia, removerDependencia };
}
